import React, { useCallback, useMemo, useState } from 'react'
import {
  Box,
  List,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material'
import { Delete as DeleteIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import ThemeKeyPhrasesModal from './ThemeKeyPhrasesModal'
import { useAppDispatch } from 'redux/Hook'
import { setSelectedTheme } from 'redux/slices/Themes'
import type {
  Theme,
  ThemesListProps,
  SelectedThemeData,
} from './ThemeKeyPhrasesModal/Types'
import { useThemesListStyles } from './Hooks/UseThemeStyles'
import DeleteThemeModal from './DeleteThemeModal'
import { ClickEvent, KeyPressEvent } from '../Helpers/Helper'
import ThemeListItem from './ThemeListItem'

const ThemesList: React.FC<ThemesListProps> = ({
  themes,
  switchToContributionsTab,
}) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const styles = useThemesListStyles()

  const [selectedTheme, setTheme] = useState<Theme | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [menuTheme, setMenuTheme] = useState<Theme | null>(null)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const openThemeModal = useCallback((theme: Theme) => {
    setTheme(theme)
    setIsModalOpen(true)
  }, [])

  const closeModal = useCallback(() => setIsModalOpen(false), [])

  const handleMenuOpen = useCallback(
    (e: ClickEvent | KeyPressEvent, theme: Theme) => {
      e.stopPropagation()
      setAnchorEl(e.currentTarget)
      setMenuTheme(theme)
    },
    [],
  )

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null)
    setMenuTheme(null)
  }, [])

  const handleDeleteClick = useCallback(() => {
    if (menuTheme) setIsDeleteModalOpen(true)
    setAnchorEl(null)
  }, [menuTheme])

  const handleDeleteModalClose = useCallback(() => {
    setIsDeleteModalOpen(false)
    handleMenuClose()
  }, [handleMenuClose])

  const handleViewContributions = useCallback(
    (filteredKeyPhrases: string[]) => {
      if (!selectedTheme) return

      const { name } = selectedTheme.attributes
      const themeData: SelectedThemeData = {
        name: name ?? '',
        key_phrases: filteredKeyPhrases.length ? filteredKeyPhrases : [''],
      }

      dispatch(setSelectedTheme(themeData))
      closeModal()
      switchToContributionsTab()
    },
    [selectedTheme, dispatch, closeModal, switchToContributionsTab],
  )

  const renderedThemesList = useMemo(() => {
    if (themes.length === 0) return null

    return (
      <List>
        {themes.map((theme) => {
          const isDeleteTheme = isDeleteModalOpen && menuTheme?.id === theme.id

          return (
            <ThemeListItem
              key={theme.id}
              theme={theme}
              isDeleteTheme={isDeleteTheme}
              onOpenThemeModal={openThemeModal}
              onMenuClick={handleMenuOpen}
            />
          )
        })}
      </List>
    )
  }, [themes, openThemeModal, handleMenuOpen, isDeleteModalOpen, menuTheme])

  return (
    <Box sx={styles.sectionBox} component="section">
      <Typography
        variant="body1"
        color="text.secondary"
        sx={styles.headerText}
        tabIndex={0}
        role="status"
        aria-live="polite"
        data-testid="themes-header"
      >
        {t('textAnalysis.aiThemes.themesListHeaderText')}
      </Typography>

      {/* Theme list */}
      {renderedThemesList}

      {/* Theme options menu */}
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleMenuClose}
        data-testid="theme-options-menu"
        disablePortal
      >
        <MenuItem onClick={handleDeleteClick}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete Theme</ListItemText>
        </MenuItem>
      </Menu>

      {/* Theme Key-Phrases modal */}
      {isModalOpen && selectedTheme && (
        <ThemeKeyPhrasesModal
          open={isModalOpen}
          themes={themes}
          themeId={selectedTheme.id ?? selectedTheme.attributes.name}
          onClose={closeModal}
          handleViewContributionsFromModal={handleViewContributions}
        />
      )}

      {/* Delete theme modal */}
      {isDeleteModalOpen && menuTheme && (
        <DeleteThemeModal
          open={isDeleteModalOpen}
          theme={menuTheme}
          onClose={handleDeleteModalClose}
        />
      )}
    </Box>
  )
}

export default React.memo(ThemesList)
